"use client";

import { useState } from "react";
import { Button } from "@/components/ui/Button";
import { formatCurrency, cn } from "@/lib/utils";

interface CafeCustomizerProps {
  item: { id: string; name: string; price: number };
  onConfirm: (
    modifiers: Array<{ modifierId: string; name: string; price: number }>,
    notes: string,
  ) => void;
  onClose: () => void;
}

interface CustomizationOption {
  id: string;
  label: string;
  icon: string;
  price: number;
}

const SIZES: CustomizationOption[] = [
  { id: "size-small", label: "S", icon: "S", price: 0 },
  { id: "size-medium", label: "M", icon: "M", price: 50 },
  { id: "size-large", label: "L", icon: "L", price: 100 },
  { id: "size-xl", label: "XL", icon: "XL", price: 150 },
];

const MILK_TYPES: CustomizationOption[] = [
  { id: "milk-whole", label: "Whole", icon: "W", price: 0 },
  { id: "milk-oat", label: "Oat", icon: "O", price: 75 },
  { id: "milk-almond", label: "Almond", icon: "A", price: 75 },
  { id: "milk-soy", label: "Soy", icon: "S", price: 50 },
  { id: "milk-coconut", label: "Coconut", icon: "C", price: 75 },
  { id: "milk-skim", label: "Skim", icon: "Sk", price: 0 },
];

const SHOT_OPTIONS: CustomizationOption[] = [
  { id: "shots-single", label: "Single", icon: "1", price: 0 },
  { id: "shots-double", label: "Double", icon: "2", price: 0 },
  { id: "shots-triple", label: "Triple", icon: "3", price: 100 },
  { id: "shots-quad", label: "Quad", icon: "4", price: 200 },
];

const TEMPS: CustomizationOption[] = [
  { id: "temp-hot", label: "Hot", icon: "H", price: 0 },
  { id: "temp-iced", label: "Iced", icon: "I", price: 0 },
  { id: "temp-blended", label: "Blended", icon: "B", price: 100 },
];

export function CafeCustomizer({ item, onConfirm, onClose }: CafeCustomizerProps) {
  const [selectedSize, setSelectedSize] = useState<string>("size-medium");
  const [selectedMilk, setSelectedMilk] = useState<string>("milk-whole");
  const [selectedShots, setSelectedShots] = useState<string>("shots-double");
  const [selectedTemp, setSelectedTemp] = useState<string>("temp-hot");

  const allOptions = [...SIZES, ...MILK_TYPES, ...SHOT_OPTIONS, ...TEMPS];
  const selectedIds = [selectedSize, selectedMilk, selectedShots, selectedTemp];
  const selectedOptions = selectedIds
    .map((id) => allOptions.find((o) => o.id === id))
    .filter(Boolean) as CustomizationOption[];

  const extraCost = selectedOptions.reduce((sum, o) => sum + o.price, 0);
  const totalPrice = item.price + extraCost;

  const handleConfirm = () => {
    const modifiers = selectedOptions
      .filter((o) => o.price > 0 || o.id !== SIZES[0].id) // include all selections for notes
      .map((o) => ({
        modifierId: o.id,
        name: o.label,
        price: o.price,
      }));

    const notes = selectedOptions.map((o) => o.label).join(", ");
    onConfirm(modifiers, notes);
  };

  const renderOptionGroup = (
    title: string,
    options: CustomizationOption[],
    selected: string,
    onSelect: (id: string) => void,
  ) => (
    <div className="space-y-2">
      <h4 className="text-xs font-semibold uppercase tracking-wider text-text-dim">{title}</h4>
      <div className="flex gap-2">
        {options.map((option) => (
          <button
            key={option.id}
            onClick={() => onSelect(option.id)}
            className={cn(
              "flex flex-col items-center justify-center rounded-xl border px-4 py-3 min-w-[60px] transition-all",
              selected === option.id
                ? "border-white bg-white/10 text-white"
                : "border-surface-border bg-surface text-text-muted hover:border-surface-border-2 hover:text-white",
            )}
          >
            <span className="text-lg font-bold">{option.icon}</span>
            <span className="text-[10px] mt-1">{option.label}</span>
            {option.price > 0 && (
              <span className="text-[10px] text-text-dim mt-0.5">+{formatCurrency(option.price)}</span>
            )}
          </button>
        ))}
      </div>
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center">
      <div className="fixed inset-0 bg-black/70 backdrop-blur-sm animate-fade-in" onClick={onClose} />
      <div className="relative z-10 w-full max-w-2xl rounded-t-2xl border-t border-x border-white/10 bg-surface/95 backdrop-blur-xl shadow-2xl animate-slide-up">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
          <div>
            <h3 className="text-lg font-semibold text-white">{item.name}</h3>
            <p className="text-sm text-text-muted">{formatCurrency(item.price)}</p>
          </div>
          <button
            onClick={onClose}
            className="rounded-xl p-2 text-text-muted hover:bg-white/5 hover:text-white"
          >
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Options */}
        <div className="px-6 py-5 space-y-5 max-h-[60vh] overflow-y-auto">
          {renderOptionGroup("Size", SIZES, selectedSize, setSelectedSize)}
          {renderOptionGroup("Milk", MILK_TYPES, selectedMilk, setSelectedMilk)}
          {renderOptionGroup("Shots", SHOT_OPTIONS, selectedShots, setSelectedShots)}
          {renderOptionGroup("Temperature", TEMPS, selectedTemp, setSelectedTemp)}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between border-t border-white/10 px-6 py-4">
          <div>
            <p className="text-sm text-text-muted">Total</p>
            <p className="text-xl font-bold text-white">{formatCurrency(totalPrice)}</p>
          </div>
          <Button variant="primary" size="lg" onClick={handleConfirm}>
            Add to Order
          </Button>
        </div>
      </div>
    </div>
  );
}
